import React, { useState } from 'react';
import { Button, message } from 'antd';
import { I18n } from '@aws-amplify/core';
import { DeleteOutlined, DownloadOutlined, CloseOutlined } from '@ant-design/icons';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useDeleteDataExtractions, useGetDataExtractionCSVToDownload } from '@/hooks';
import { DeleteDataExtractionModal } from '@/components/Settings/DataExtraction/Modals';
import { ORIGINAL_STATUS_COMPLETED } from '@/context/DataExtraction/constants';
import { downloadFile } from '@/utils';
import { FloatingActions, ActionsBar } from './styles';

interface FloatingBarProps {
	totalSelectedCount: number;
	onClearSelection: () => void;
	selectedRowKeys: React.Key[];
	selectedRows: Array<{
		id: string;
		organization_id: string;
		company_id: string;
		status: string;
		originalStatus?: string;
	}>;
}

export function FloatingBar({
	totalSelectedCount,
	onClearSelection,
	selectedRowKeys,
	selectedRows
}: Readonly<FloatingBarProps>) {
	const [modalOpen, setModalOpen] = useState(false);
	const { organization, company } = useApplicationContext();
	const { mutateAsync: deleteDataExtractions, isLoading } = useDeleteDataExtractions();
	const downloadCSV = useGetDataExtractionCSVToDownload();

	if (totalSelectedCount === 0) return null;

	const completedRows = selectedRows.filter((row) => {
		return row.originalStatus === ORIGINAL_STATUS_COMPLETED;
	});

	const hasCompletedExtractions = completedRows.length > 0;

	const handleDownload = async () => {
		if (!hasCompletedExtractions) return;

		for (const row of completedRows) {
			try {
				const response = await downloadCSV.mutateAsync({
					organization_id: row.organization_id,
					company_id: row.company_id,
					data_extraction_id: row.id
				});

				downloadFile(response.url);
			} catch (error) {
				message.error(I18n.get('Failed to download the data extraction file. Please try again.'));
			}
		}
	};

	function handleDeleteClick() {
		setModalOpen(true);
	}

	async function handleConfirmDelete(): Promise<void> {
		await deleteDataExtractions({
			ids: selectedRowKeys.map(String),
			organization_id: organization?.id || '',
			company_id: company?.id || ''
		});
		setModalOpen(false);
		onClearSelection();
	}

	function handleCloseModal() {
		setModalOpen(false);
	}

	return (
		<FloatingActions>
			<ActionsBar>
				<Button
					type="text"
					icon={<DownloadOutlined />}
					onClick={handleDownload}
					disabled={!hasCompletedExtractions}
					title={
						hasCompletedExtractions
							? `${I18n.get('Download')} ${completedRows.length} ${I18n.get('completed items')}`
							: I18n.get('No completed extractions available for download')
					}
				/>
				<Button
					type="text"
					danger
					icon={<DeleteOutlined />}
					onClick={handleDeleteClick}
					title={`${I18n.get('Delete')} ${totalSelectedCount} ${I18n.get('items')}`}
				/>
				<Button
					type="text"
					icon={<CloseOutlined />}
					onClick={onClearSelection}
					title={I18n.get('Clear selection')}
				/>
			</ActionsBar>
			<DeleteDataExtractionModal
				visible={modalOpen}
				onCancel={handleCloseModal}
				onConfirm={handleConfirmDelete}
				customMessage={I18n.get('Do you want to delete the selected extractions?')}
				loading={isLoading}
			/>
		</FloatingActions>
	);
}
